import React, { FunctionComponent } from 'react';
import styled from 'styled-components';

import THEME from 'theme';
import { Heading, Text, Icon } from 'components/chroma';
import { productDisclosureStatementUrl } from 'scenes/common/helpers/contacts';
import { replaceSpaces } from 'scenes/common/helpers/text';
import { MotorCoverType } from 'scenes/motor/types';
import {
  Included,
  KeyDifference,
  KeyDifferenceItem,
  thirdPartyKeyDifferences,
} from './keyDifferencesData';

const Container = styled.div`
  text-align: center;
  th {
    padding: 0.5rem 0 0.5rem 0;
  }
  th,
  td {
    :nth-child(2) {
      border-left: 1px solid ${THEME.FIELD.BORDER.LIGHT};
    }
  }
`;

const DifferenceHeader = styled.th`
  div {
    padding: 0.5rem;
    background-color: ${THEME.BACKGROUND.HEADING};
  }
`;

const IncludedIconWrapper = styled.div`
  height: 1.5rem;
`;

const IncludedIcon = styled(Icon)`
  color: ${THEME.ICON.VALID};
`;

const NotIncludedIcon = styled(Icon)`
  color: ${THEME.ICON.INVALID};
`;

const OffscreenTextWrapper = styled.div`
  position: absolute;
  left: -10000px;
  top: auto;
  width: 1px;
  height: 1px;
  overflow: hidden;
`;

const DisclaimersWrapper = styled.div`
  margin-top: 2rem;
`;

const DifferenceItem: FunctionComponent<{
  item: KeyDifferenceItem;
  label: string;
  index: number;
  headers: [MotorCoverType, MotorCoverType];
}> = ({ item, label, index, headers }) => {
  let IconComponent: FunctionComponent;
  let ScreenReaderText: FunctionComponent;
  switch (item.included) {
    case Included.Yes:
      IconComponent = () => <IncludedIcon name="check" ariaLabel="Included" />;
      ScreenReaderText = () => (
        <OffscreenTextWrapper>Included</OffscreenTextWrapper>
      );
      break;
    case Included.No:
      IconComponent = () => (
        <NotIncludedIcon name="times" ariaLabel="Not included" />
      );
      ScreenReaderText = () => (
        <OffscreenTextWrapper>Not Included</OffscreenTextWrapper>
      );
      break;
    case Included.Optional:
      IconComponent = () => (
        <Text weight={400} margin={{ bottom: 0 }}>
          Optional extra
        </Text>
      );
      ScreenReaderText = () => (
        <OffscreenTextWrapper>Optional Extra</OffscreenTextWrapper>
      );
      break;
    default:
      return <></>;
  }

  return (
    <td headers={replaceSpaces(`${headers[index]}_${label}`)}>
      <IncludedIconWrapper>
        <IconComponent />
        <ScreenReaderText />
      </IncludedIconWrapper>
      {item?.additionalText && (
        <Text margin={{ bottom: 0 }}>{item.additionalText}</Text>
      )}
    </td>
  );
};

const TableHeader: FunctionComponent<{
  headers: [MotorCoverType, MotorCoverType];
}> = ({ headers }) => (
  <thead>
    <tr>
      <th scope="col" id={replaceSpaces(headers[0])}>
        <Text weight={700} margin={{ bottom: 0 }}>
          {headers[0]}
        </Text>
      </th>
      <th scope="col" id={replaceSpaces(headers[1])}>
        <Text weight={700} margin={{ bottom: 0 }}>
          {headers[1]}
        </Text>
      </th>
    </tr>
  </thead>
);

const TableItem: FunctionComponent<{
  difference: KeyDifference;
  headers: [MotorCoverType, MotorCoverType];
}> = ({ difference, headers }) => (
  <>
    <tr id={replaceSpaces(difference.label)}>
      <DifferenceHeader colSpan={2}>
        <Text weight={700} margin={{ bottom: 0 }}>
          {difference.label}
        </Text>
      </DifferenceHeader>
    </tr>
    <tr>
      {difference.items.map((item, index) => (
        <DifferenceItem
          key={replaceSpaces(`${headers[index]}${difference.label}`)}
          item={item}
          label={difference.label}
          index={index}
          headers={headers}
        />
      ))}
    </tr>
  </>
);

export const KeyDifferences: FunctionComponent = () => (
  <>
    <Heading size={4} margin={{ bottom: 2 }}>
      Key Product Differences
    </Heading>
    <Container>
      <table>
        <TableHeader headers={thirdPartyKeyDifferences.headers} />
        <tbody>
          {thirdPartyKeyDifferences.differences.map((difference) => (
            <TableItem
              key={replaceSpaces(difference.label)}
              difference={difference}
              headers={thirdPartyKeyDifferences.headers}
            />
          ))}
        </tbody>
      </table>
    </Container>

    <DisclaimersWrapper>
      <Text>
        Summary only. Conditions apply. Refer to{' '}
        <a
          href={productDisclosureStatementUrl()}
          target="_blank"
          rel="noopener noreferrer"
        >
          Product Disclosure Statement
        </a>{' '}
        for full details.
      </Text>
    </DisclaimersWrapper>
  </>
);

export default KeyDifferences;
